import _ from "lodash";
import {
  Article,
  ArticleSource,
  Article_InventoryType,
} from "../../../api/schema";
import { ModalInputType } from "../../../components/form/Form";
import { FormInputFields } from "../../../components/form/InputFormBuilder";
import { UserLanguage } from "../../../lang/lang";
import {
  GroupPrettyRender,
  LocationPrettyRender,
} from "../../../utils/PrettyRenderer";
import { ArticleListResponse, ArticleViewResponse } from "../helper/graphql";

export const CreateArticleModalKeys = (
  articleViewData: ArticleListResponse | null
): FormInputFields<Article> => ({
  name: {
    type: ModalInputType.String,
    label: UserLanguage().data.name,
  },
  matchcode: {
    type: ModalInputType.String,
    label: UserLanguage().data.article.matchcode,
  },
  display_name: {
    type: ModalInputType.String,
    label: UserLanguage().data.display_name,
  },
  footprint: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.footprint.label,
    items: articleViewData?.footprints,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.footprint.label
    ),
    itemKey: "name",
    icon: "box",
  },
  article_category: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.articleCategory.label,
    items: articleViewData?.articleCategories,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.articleCategory.label
    ),
    textRenderer: (ac) =>
      ac !== null
        ? _.get(UserLanguage().data.article_categories, ac.name, ac.name)
        : "",
    itemKey: "name",
    icon: "folder-open",
  },
  location: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.location.label,
    items: articleViewData?.locations,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.location.label
    ),
    textRenderer: (l) => LocationPrettyRender(l),
    itemKey: "name",
    icon: "map-marker",
  },
  product_group: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.product_group.label,
    items: articleViewData?.productGroups,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.product_group.label
    ),
    textRenderer: (l) => GroupPrettyRender(l),
    itemKey: "name",
    icon: "folder-open",
  },
  inventory_type: {
    type: ModalInputType.ItemSelect,
    items: _.values(Article_InventoryType),
    label: UserLanguage().data.inventory_type.label,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.inventory_type.label
    ),
    defaultValue: Article_InventoryType.static,
  },
  datasheet_url: {
    type: ModalInputType.String,
    label: UserLanguage().data.article.datasheet_url,
    icon: "document",
  },
  picture: {
    type: ModalInputType.String,
    label: UserLanguage().data.article.picture,
    icon: "mugshot",
  },
  description: {
    type: ModalInputType.Text,
    label: UserLanguage().data.description,
    icon: "clipboard",
  },
});

export const UpdateArticleModalKeys = (
  partViewData: ArticleListResponse | null
): FormInputFields<Article> => ({
  id: {
    type: ModalInputType.String,
    readonly: true,
    label: UserLanguage().data.id,
  },
  name: {
    type: ModalInputType.String,
    icon: "id-number",
    label: UserLanguage().data.name,
  },
  matchcode: {
    type: ModalInputType.String,
    icon: "id-number",
    label: UserLanguage().data.article.matchcode,
  },
  display_name: {
    type: ModalInputType.String,
    icon: "chevron-right",
    label: UserLanguage().data.display_name,
  },
  footprint: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.footprint.label,
    items: partViewData?.footprints,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.footprint.label
    ),
    itemKey: "name",
    icon: "box",
  },
  article_category: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.articleCategory.label,
    items: partViewData?.articleCategories,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.articleCategory.label
    ),
    itemKey: "name",
    icon: "folder-open",
  },
  location: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.location.label,
    items: partViewData?.locations,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.location.label
    ),
    textRenderer: (l) => LocationPrettyRender(l),
    itemKey: "name",
    icon: "map-marker",
  },
  product_group: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.product_group.label,
    items: partViewData?.productGroups,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.product_group.label
    ),
    textRenderer: (l) => GroupPrettyRender(l),
    itemKey: "name",
    icon: "map-marker",
  },
  inventory_type: {
    type: ModalInputType.ItemSelect,
    items: _.values(Article_InventoryType),
    label: UserLanguage().data.inventory_type.label,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.inventory_type.label
    ),
    defaultValue: Article_InventoryType.static,
  },
  datasheet_url: {
    type: ModalInputType.String,
    label: UserLanguage().data.article.datasheet_url,
    icon: "document",
  },
  picture: {
    type: ModalInputType.String,
    label: UserLanguage().data.article.picture,
    icon: "mugshot",
  },
  description: {
    type: ModalInputType.Text,
    label: UserLanguage().data.description,
    icon: "clipboard",
  },
});

export const CreateArticleSourceModalKeys = (
  data: ArticleViewResponse | null
): FormInputFields<ArticleSource> => ({
  order_number: {
    type: ModalInputType.String,
    icon: "id-number",
    label: UserLanguage().data.articleSource.order_number,
  },
  moq: {
    type: ModalInputType.Number,
    label: UserLanguage().data.moq,
    defaultValue: 1,
  },
  quantity_scale: {
    type: ModalInputType.QuantityScale,
    label: UserLanguage().data.quantity_scale.label,
    defaultValue: [],
  },
  url: {
    type: ModalInputType.String,
    icon: "link",
    label: UserLanguage().data.articleSource.url,
  },
  distributor: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.distributor.label,
    items: data?.distributors,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.distributor.label
    ),
    itemKey: "name",
    icon: "flow-review-branch",
  },
  article: {
    type: ModalInputType.FixedItem,
    defaultValue: data?.article,
  },
});

export const UpdateArticleSourceModalKeys = (
  data: ArticleViewResponse | null
): FormInputFields<ArticleSource> => ({
  id: {
    type: ModalInputType.String,
    readonly: true,
    label: UserLanguage().data.id,
  },
  order_number: {
    type: ModalInputType.String,
    icon: "id-number",
    label: UserLanguage().data.articleSource.order_number,
  },
  url: {
    type: ModalInputType.String,
    icon: "link",
    label: UserLanguage().data.articleSource.url,
  },
  distributor: {
    type: ModalInputType.ItemSelect,
    label: UserLanguage().data.distributor.label,
    items: data?.distributors,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.distributor.label
    ),
    itemKey: "name",
    icon: "flow-review-branch",
  },
  moq: {
    type: ModalInputType.Number,
    label: UserLanguage().data.moq,
    defaultValue: 1,
  },
  quantity_scale: {
    type: ModalInputType.QuantityScale,
    label: UserLanguage().data.quantity_scale.label,
    defaultValue: [],
  },
  article: {
    type: ModalInputType.FixedItem,
  },
});
